// Platform / control-plane API. Every endpoint is SuperAdmin-only;
// callers should sit behind PermissionGuard[SUPER_ADMIN_MANAGE].

import apiService from "@/lib/api";

const PLATFORM_BASE = "/api/v2/platform";

export const PlatformService = {
  // ------------------------------------------------------------------
  // RLS
  // ------------------------------------------------------------------

  // includeUnpoliced adds a per-database scan for tables that have row
  // security on and no policy at all — deny-everything for the app role. It
  // costs an extra query per database, so the dashboard's own load leaves it
  // off and the health check asks for it explicitly.
  fetchRlsStatus: async ({ includeUnpoliced = false } = {}) => {
    try {
      const params = {};
      if (includeUnpoliced) params.includeUnpoliced = true;
      const response = await apiService.getSecure(`${PLATFORM_BASE}/rls/status`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching RLS status:", error);
      throw error;
    }
  },

  // The Alembic revision graph, newest first. Each entry carries its migration's
  // own docstring as title + description, and the operations parsed out of its
  // code — which say what it does even for the 43 files whose docstring is one
  // line. Read from code, not from any database, and identical for every one of
  // them, so it is fetched once and joined against a row's `pendingRevisions`.
  fetchAlembicRevisions: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/alembic/revisions`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Alembic revisions:", error);
      throw error;
    }
  },

  // The real SQL one revision would run, rendered by Alembic's own offline mode
  // from the same code that runs in production. No database is touched.
  //
  // `available: false` is a normal answer, not an error: a merge revision has no
  // SQL of its own, and 13 migrations read the database to decide what to do and
  // cannot render without running. Fall back to the revision's parsed
  // `upgradeOperations` in that case. direction = 'upgrade' | 'downgrade'.
  fetchRevisionSql: async (revision, direction = "upgrade") => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/alembic/revisions/${revision}/sql`, { params: { direction } });
      return response.data;
    } catch (error) {
      console.error("Error fetching revision SQL:", error);
      throw error;
    }
  },

  // Does this database's real schema match what the code expects?
  //
  // `alembic_version` records a claim; this checks it. Reads the whole
  // catalogue of one database, so it is asked for on demand and never as part
  // of the fleet snapshot. target = 'platform' | 'tenant:<uuid>'.
  fetchSchemaDiff: async (target) => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/schema/diff`, {
        params: { target },
        timeout: 120_000, // a full catalogue read on a large database
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching schema diff:", error);
      throw error;
    }
  },

  // alembic upgrade head on dedicated tenant DBs (platform excluded).
  // target = 'all' | 'tenant:<uuid>'.
  applyAlembic: async ({ target } = {}) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/alembic/apply`, { target });
      return response.data;
    } catch (error) {
      console.error("Error applying Alembic:", error);
      throw error;
    }
  },

  // `version` renders an archived version instead of the one the code is on,
  // so a rollback can be read before it is run. Omit it for the current
  // version, which is what Apply would execute.
  previewRls: async ({ target, force = false, version } = {}) => {
    try {
      // No `dry_run`: the server declared the flag and read it nowhere, so it
      // is gone from `RlsApplyRequest`. This endpoint IS the dry run.
      const payload = { target, force };
      if (version != null) payload.version = version;
      const response = await apiService.postSecure(`${PLATFORM_BASE}/rls/preview`, payload);
      return response.data;
    } catch (error) {
      console.error("Error previewing RLS:", error);
      throw error;
    }
  },

  applyRls: async ({ target, force = false, stampOnly = false } = {}) => {
    try {
      // 6 min timeout > backend's 5 min per-statement cap.
      // stampOnly = skip DDL, just write rls_meta (ms, no locks).
      const response = await apiService.postSecure(
        `${PLATFORM_BASE}/rls/apply`,
        // `force` and `stamp_only` are honoured; `dry_run` never was, and is
        // no longer declared. Preview is the dry run.
        { target, force, stamp_only: stampOnly },
        { timeout: 360_000 }, // 6 minutes
      );
      return response.data;
    } catch (error) {
      console.error("Error applying RLS:", error);
      throw error;
    }
  },

  // Fleet reconcile — Alembic upgrade then RLS per DB. Skips RLS
  // if Alembic failed. 10-min timeout.
  reconcileFleet: async () => {
    try {
      const response = await apiService.postSecure(
        `${PLATFORM_BASE}/reconcile`,
        {},
        { timeout: 600_000 }, // 10 minutes for a whole fleet reconcile
      );
      return response.data;
    } catch (error) {
      console.error("Error reconciling fleet:", error);
      throw error;
    }
  },

  // alembic downgrade <targetRevision> (default '-1').
  // target = 'all' | 'tenant:<uuid>'.
  downgradeAlembic: async ({ target, targetRevision = "-1" } = {}) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/alembic/downgrade`, { target, target_revision: targetRevision }, { timeout: 360_000 });
      return response.data;
    } catch (error) {
      console.error("Error downgrading Alembic:", error);
      throw error;
    }
  },

  applyPermissionsSync: async ({ target } = {}) => {
    // 6 min timeout — sync may touch hundreds of rows per DB.
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/permissions/apply`, { target }, { timeout: 360_000 });
      return response.data;
    } catch (error) {
      console.error("Error applying permissions sync:", error);
      throw error;
    }
  },

  // Canary rollout step 1 — canary tenant configured via
  // PLATFORM_RLS_CANARY_TENANT_SLUG on the backend.
  applyRlsCanary: async () => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/rls/apply-canary`, {}, { timeout: 360_000 });
      return response.data;
    } catch (error) {
      console.error("Error applying RLS to canary:", error);
      throw error;
    }
  },

  applyRlsRemaining: async () => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/rls/apply-remaining`, {}, { timeout: 600_000 });
      return response.data;
    } catch (error) {
      console.error("Error applying RLS to remaining (post-canary):", error);
      throw error;
    }
  },

  // Archived RLS versions — { current, versions[] }.
  fetchRlsVersions: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/rls/versions`);
      return response.data;
    } catch (error) {
      console.error("Error fetching RLS versions:", error);
      throw error;
    }
  },

  // Apply an archived RLS version to one dedicated tenant (rollback path).
  applyRlsVersionToTenant: async (tenantId, version) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/tenants/${tenantId}/rls/apply-version`, { version }, { timeout: 360_000 });
      return response.data;
    } catch (error) {
      console.error("Error applying RLS version to tenant:", error);
      throw error;
    }
  },

  // Fleet-wide RLS rollback. 10-min timeout for large fleets.
  applyRlsVersionToAll: async (version) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/rls/apply-version-all`, { version }, { timeout: 600_000 });
      return response.data;
    } catch (error) {
      console.error("Error applying RLS version to fleet:", error);
      throw error;
    }
  },

  // Permissions catalog sync (manual, one-click per DB).
  fetchPermissionsStatus: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/permissions/status`);
      return response.data;
    } catch (error) {
      console.error("Error fetching permissions status:", error);
      throw error;
    }
  },

  previewPermissionsSync: async ({ target } = {}) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/permissions/preview`, { target });
      return response.data;
    } catch (error) {
      console.error("Error previewing permissions sync:", error);
      throw error;
    }
  },

  // Which permission codes are drifted on one dedicated database. The status
  // call gives the three counts for the whole fleet; this names the codes
  // behind one row's counts. Fetched only when a reader opens the detail —
  // status already opens a connection per database.
  fetchPermissionsDetail: async (tenantId) => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/tenants/${tenantId}/permissions/detail`);
      return response.data;
    } catch (error) {
      console.error("Error fetching permissions detail:", error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // Tenant-DB registry
  // ------------------------------------------------------------------

  fetchTenantDatabases: async ({ page = 1, per_page = 20, pagination = true, search = "", ...params } = {}) => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/tenant-databases`, { params: { page, per_page, pagination, search, ...params } });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant databases:", error);
      throw error;
    }
  },

  fetchTenantDatabaseDetail: async (tenantId) => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/tenant-databases/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant database detail:", error);
      throw error;
    }
  },

  reloadEngine: async (tenantId) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/tenant-databases/${tenantId}/reload-engine`);
      return response.data;
    } catch (error) {
      console.error("Error reloading tenant engine:", error);
      throw error;
    }
  },

  preflightAdminUrl: async (adminUrl = null) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/tenant-databases/preflight`, { admin_url: adminUrl });
      return response.data;
    } catch (error) {
      console.error("Error running admin URL preflight:", error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // Migration log
  // ------------------------------------------------------------------

  fetchMigrations: async ({ tenantId, kind, status, fromDate, toDate, page = 1, per_page = 50, pagination = true } = {}) => {
    try {
      const params = {
        page,
        per_page,
        pagination,
      };
      if (tenantId) params.tenantId = tenantId;
      if (kind) params.kind = kind;
      if (status) params.status = status;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const response = await apiService.getSecure(`${PLATFORM_BASE}/migrations`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching migrations:", error);
      throw error;
    }
  },

  // Per-tenant migration log (for the row-level history drawer).
  fetchTenantMigrations: async (tenantId, { kind, status, page = 1, per_page = 20, pagination = true } = {}) => {
    try {
      const params = { page, per_page, pagination };
      if (kind) params.kind = kind;
      if (status) params.status = status;
      const response = await apiService.getSecure(`${PLATFORM_BASE}/tenants/${tenantId}/migrations`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant migrations:", error);
      throw error;
    }
  },

  // Re-run a failed migration row. Backend writes a fresh audit row.
  retryTenantMigration: async (tenantId, migrationId) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/tenants/${tenantId}/migrations/${migrationId}/retry`, {}, { timeout: 360_000 });
      return response.data;
    } catch (error) {
      console.error("Error retrying migration:", error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // Platform audit log
  // ------------------------------------------------------------------

  fetchPlatformAuditLogs: async ({ eventType, targetTenantId, actorUserId, fromDate, toDate, page = 1, per_page = 50, pagination = true } = {}) => {
    try {
      const params = { page, per_page, pagination };
      if (eventType) params.eventType = eventType;
      if (targetTenantId) params.targetTenantId = targetTenantId;
      if (actorUserId) params.actorUserId = actorUserId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const response = await apiService.getSecure(`${PLATFORM_BASE}/audit-logs`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching platform audit logs:", error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // SuperAdmin invitations
  // ------------------------------------------------------------------

  // Invite a SuperAdmin by email. AD users → use the promote flow at /admin/super-admins.
  inviteSuperAdmin: async ({ name, email }) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/superadmins/invite`, { name, email });
      return response.data;
    } catch (error) {
      console.error("Error inviting SuperAdmin:", error);
      throw error;
    }
  },

  fetchSuperAdminInvitations: async ({ search = "", status = "", page = 1, per_page = 20, pagination = true, sort_by = "", sort_order = "" } = {}) => {
    try {
      const params = { page, per_page, pagination };
      if (search) params.search = search;
      if (status) params.status = status;
      // Dropped before this, so every sortable header on the SuperAdmin
      // list changed its arrow and reordered nothing.
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;
      const response = await apiService.getSecure(`${PLATFORM_BASE}/superadmins/invitations`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching SuperAdmin invitations:", error);
      throw error;
    }
  },

  revokeSuperAdminInvitation: async (invitationId, reason = null) => {
    try {
      const params = {};
      if (reason) params.reason = reason;
      const response = await apiService.delete(`${PLATFORM_BASE}/superadmins/invitations/${invitationId}`, { params });
      return response.data;
    } catch (error) {
      console.error("Error revoking SuperAdmin invitation:", error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // Platform-owned sourcing API keys
  //
  // These are OUR keys — one row per platform, shared by every tenant whose
  // "uses own API keys" switch is off. Editing one affects all of them from
  // their next campaign run, which is why the list response also reports how
  // many tenants that currently is.
  //
  // Responses are snake_case: these nest the sourcing module's
  // PlatformConfigResponse, so the whole payload matches it rather than the
  // camelCase used elsewhere in this file.
  // ------------------------------------------------------------------

  // -> { platform_managed_tenants, own_keys_tenants, platforms: [...] }
  // Each platform carries `credential_schema`, so the form is generated from
  // the response instead of hardcoded per provider.
  fetchSourcingKeys: async () => {
    try {
      const response = await apiService.getSecure(`${PLATFORM_BASE}/sourcing-keys`);
      return response.data;
    } catch (error) {
      console.error("Error fetching platform sourcing keys:", error);
      throw error;
    }
  },

  // Send `credentials` only when a new value was actually entered. Omitting it
  // leaves the stored key untouched — which is what makes the enable/disable
  // toggle safe to use mid-rotation (off -> replace -> test -> on) without an
  // in-flight campaign picking up a half-written credential.
  updateSourcingKey: async (platform, { isEnabled, credentials, notes } = {}) => {
    try {
      const payload = {};
      if (isEnabled !== undefined) payload.is_enabled = isEnabled;
      if (credentials !== undefined) payload.credentials = credentials;
      if (notes !== undefined) payload.notes = notes;

      const response = await apiService.patch(`${PLATFORM_BASE}/sourcing-keys/${platform}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updating platform sourcing key:", error);
      throw error;
    }
  },

  // Probes the platform with OUR key and records the result on the row.
  // Refuses to probe when nothing is configured rather than reporting a
  // misleading "connection OK" — and for the metered providers that also
  // avoids spending a billed call to learn nothing.
  testSourcingKey: async (platform) => {
    try {
      const response = await apiService.postSecure(`${PLATFORM_BASE}/sourcing-keys/${platform}/test`, {});
      return response.data;
    } catch (error) {
      console.error("Error testing platform sourcing key:", error);
      throw error;
    }
  },
};
